import { React, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, TextField, InputAdornment, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { styled } from "@mui/material/styles";
import * as TextVariables from "../../../assets/Variables/TextVariables.js";
import * as ColorsVariables from "../../../assets/Variables/ColorsVariables.js";
import { Spaces } from "../../../assets/Variables/SpaceVariables.js";
import Colors from "../../../assets/Variables/ColorsVariables.js";
import MultiSelector from "../../Generals/Selectors/MultiSelector.js";
import Button3px from "../../Generals/Buttons/3pxButton.js";
import followerServices from "../../../services/follower"; // Importación del servicio
import { SetSymbols } from "../../../Tools/LocalStorage.js";
import { useBreakpoint } from "../../../config/BreakpointContext.js";
import TextIconButton from "../../Generals/Buttons/TextIconButton.js";
import AssetSymbolFilter from "../../Generals/Modals/AssetSymbolFilter.js";
import { useLocation } from 'react-router-dom';
import TextInputWithChips from "../../Generals/Inputs/TextInputWithChip.js";

const StyleButton = styled(Button)(({ active }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  backgroundColor: active ? "#ECF1F7" : "rgba(255, 255, 255, 0)",
  textTransform: "none",
  maxHeight: "33px",
  marginRight: 1,
  padding: "12px",
  border: active ? "2px solid #A0AFBC" : "none",
  borderRadius: "8px",
  paddingLeft: "10px",
  paddingRight: "10px",
  fontFamily: TextVariables.Caption_1_Bold,
  color: "rgba(55, 55, 55, 1)",
}));

const FilterComponent = ({
  selectedAssets,
  selectedInstruments,
  handleAssetChange,
  handleInstrumentChange,
  search,
  handleSearchChange,
  handleSearch,
  handleFilterChange,
  sortOrder,
}) => {
  const location = useLocation();
  const { isXs, isMd } = useBreakpoint();
  const [t] = useTranslation("global");
  const [symbolsData, setSymbolsData] = useState({});
  const [filteredInstruments, setFilteredInstruments] = useState([]);
  const [openFilter, setOpenFilter] = useState(false);
  //#region "useEffect"R
  // Recuperar selecciones guardadas al montar el componente
  useEffect(() => {
    const savedSymbols = localStorage.getItem("symbolsData");

    if (savedSymbols) {
      setSymbolsData(JSON.parse(savedSymbols));
    } else {
      const fetchSymbols = async () => {
        try {
          const response = await followerServices.getAllSymbols();
          SetSymbols(response);
          const symbols = response.data.data;
          localStorage.setItem("symbolsData", JSON.stringify(symbols));
          setSymbolsData(symbols);
        } catch (error) {
          console.error("Error fetching symbols", error);
        }
      };

      fetchSymbols();
    }
  }, []);

  const handleCloseNotification = (prevModalProps) => {
    setOpenFilter(false);
  };

  const handleOpenFIlter = () => {
    setOpenFilter(true);
  };
  
  useEffect(() => {
    handleAssetChange({ target: { value: [] } });
    handleInstrumentChange([]);
  }, [location.pathname]);

  // Filtrar las divisas en función de los activos seleccionados
  useEffect(() => {
    if (
      selectedAssets.length === 0 ||
      !symbolsData ||
      Object.keys(symbolsData).length === 0
    ) {
      setFilteredInstruments([]);
      handleInstrumentChange([]);
    } else {
      const availableCurrencies = selectedAssets.reduce((acc, assetCategory) => {
        const symbolsForCategory = symbolsData[assetCategory.toLowerCase()] || [];
        return [...new Set([...acc, ...symbolsForCategory])];
      }, []);
      setFilteredInstruments(availableCurrencies);
    }
  }, [selectedAssets, symbolsData]);

  //#endregion
  
  //#region "HTML"
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMd ? "row" : "column",
        justifyContent: "space-between",
        alignItems: isMd ? "flex-end" : "flex-start",
        width: "100%",
        gap: Spaces.Size_3,
      }}
    >
      <AssetSymbolFilter
        open={openFilter}
        handleAssetChange={handleAssetChange}
        handleInstrumentChange={handleInstrumentChange}
        selectedAssets={selectedAssets}
        filteredInstruments={filteredInstruments}
        selectedInstruments={selectedInstruments}
        onClose={handleCloseNotification}
        handleSearch = {handleSearch}
      />
      {isMd && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: "16px",
          }}
        >
          {/* Box Assets */}
          <MultiSelector
            title={t("follower.Asset")}
            options={["Forex", "CFDs", "Metals", "Cryptos", "Stocks"]}
            selectedValues={selectedAssets}
            handleChange={handleAssetChange}
            width="184px"
          />
          {/* Box Instruments */}
          <TextInputWithChips
            title={"Instruments"}
            options={filteredInstruments}
            selectedOptions={selectedInstruments}
            onSelectedOptions={handleInstrumentChange}
          />

          {/* Box Search */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              flexDirection: "row",
              gap: Spaces.Size_2,
            }}
          >
            <TextField
              value={search}
              onChange={handleSearchChange}
              placeholder={t("follower.Search by strategy name")}
              sx={{
                width: "184px",
                height: "38px",
                padding: "0px",
                fontFamily: TextVariables.Overline,
                border: `1px solid ${Colors.Secondary_400}`,
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  "& .MuiInputBase-input": {
                    padding: Spaces.Size_3,
                    fontFamily: TextVariables.Overline,
                    color: ColorsVariables.Colors.Secondary_900,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    sx={{
                      marginRight: "0px",
                    }}
                  >
                    <SearchIcon padding={0} />
                  </InputAdornment>
                ),
                sx: {
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                },
              }}
            />
            <Button3px
              label={"Search"}
              color={"White"}
              backgroundColor={Colors.Error_500}
              HandleOnClick={handleSearch}
            ></Button3px>
          </Box>
        </Box>
      )}
      {!isMd && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            gap: "16px",
          }}
        >
          {/* Box Search */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              flexDirection: "row",
              gap: Spaces.Size_2,
            }}
          >
            <TextField
              value={search}
              onChange={handleSearchChange}
              placeholder={t("follower.Search by strategy name")}
              sx={{
                width: "184px",
                height: "38px",
                padding: "0px",
                fontFamily: TextVariables.Overline,
                border: `1px solid ${Colors.Secondary_400}`,
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  "& .MuiInputBase-input": {
                    padding: Spaces.Size_3,
                    fontFamily: TextVariables.Overline,
                    color: ColorsVariables.Colors.Secondary_900,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    sx={{
                      marginRight: "0px",
                    }}
                  >
                    <SearchIcon padding={0} />
                  </InputAdornment>
                ),
                sx: {
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                },
              }}
            />
            <Button3px
              label={"Search"}
              color={"White"}
              backgroundColor={Colors.Error_500}
              HandleOnClick={handleSearch}
            />
            <Box 
              sx={{
                display: "flex"
              }}
            >
              <TextIconButton
                logo={"filter_alt"}
                label={"Filters"}
                HandleOnClick={handleOpenFIlter}
              ></TextIconButton>
            </Box>
          </Box>
        </Box>
      )}

      {/* Box Botones del Filter */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {["All", "Most popular", "Low fees", "High risk", "Low risk"].map(
          (filterName) => (
            <StyleButton
              sx={{
                minHeight:'40px',
                padding: "10px",
                borderRadius: "8px",
                border:
                  sortOrder.toLowerCase() ===
                  filterName.toLowerCase().replace(" ", "_")
                    ? "2px solid #A0AFBC"
                    : "0px solid",
                fontFamily: TextVariables.Caption_1_Bold,
                background:
                  sortOrder.toLowerCase() ===
                  filterName.toLowerCase().replace(" ", "_")
                    ? "#ECF1F7"
                    : "transparent",
              }}
              key={filterName}
              onClick={() => handleFilterChange(filterName)}
            >
              {t(`follower.${filterName}`)} {/* Traducir */}
            </StyleButton>
          )
        )}
      </Box>
    </Box>
  );
  //#endregion
};

export default FilterComponent;
